import './selectorHeader.scss';

import { ReactNode } from 'react';
import { FormattedMessage } from 'react-intl';
import { Button, ButtonColor, ButtonVariant, Size } from '@eliberty/design-system-react';

/**
 * Presentational atom that renders the header row of a selector.
 *
 * Displays the selector label, selected value (via `children`), and an
 * optional Edit/Cancel toggle button (collapse mode only).
 *
 * In **collapse** mode (`isClickable=true`), the header renders a `<button>`
 * for accessibility, allowing the user to re-open a collapsed selector.
 *
 * In **popover** mode, the header is passive — click handling is delegated to
 * `SelectorLayout`'s `Popover.Target` wrapper which surrounds this component.
 *
 * @see SelectorLayout — structural companion that manages the Collapse/Popover
 *   rendering mechanism.
 */
interface SelectorHeaderProps {
  isClosed: boolean;
  isClickable: boolean;
  onToggle: () => void;
  onHeaderClick?: () => void;
  showToggleButton?: boolean;
  disabled?: boolean;
  children: ReactNode;
}

const SelectorHeader = ({
  isClosed,
  isClickable,
  onToggle,
  onHeaderClick,
  showToggleButton = true,
  disabled = false,
  children,
}: SelectorHeaderProps) => {
  const headerClassName = `SelectorHeader${isClickable ? ' SelectorHeader-clickable' : ''}`;

  const handleHeaderClick = () => {
    if (isClickable && onHeaderClick) {
      onHeaderClick();
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (isClickable && onHeaderClick && (e.key === 'Enter' || e.key === ' ')) {
      e.preventDefault();
      onHeaderClick();
    }
  };

  const toggleButton = showToggleButton && !disabled && (
    <Button
      size={Size.S}
      color={ButtonColor.Primary}
      variant={ButtonVariant.Ghost}
      onClick={(e) => {
        e.stopPropagation();
        onToggle();
      }}
    >
      {isClosed ? (
        <FormattedMessage id='edit' defaultMessage='Edit' />
      ) : (
        <FormattedMessage id='cancel' defaultMessage='Cancel' />
      )}
    </Button>
  );

  if (isClickable) {
    return (
      <div className={headerClassName}>
        <button
          type='button'
          className='SelectorHeader_clickableArea'
          onClick={handleHeaderClick}
          onKeyDown={handleKeyDown}
        >
          {children}
        </button>
        {toggleButton}
      </div>
    );
  }

  return (
    <div className={headerClassName}>
      {children}
      {toggleButton}
    </div>
  );
};

export default SelectorHeader;
